// Direct imports
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Node } from "@babylonjs/core/node";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Graphic } from "./graphic";
import { FrameData } from "../scenedata/behaviors/framedata";
import { loadModel } from "../utils/ModelLoader";
import { TraceBehavior } from "./tracebehavior";
import { drivesValue, drivingHexColor } from "./bindingValues";
import { SceneObject } from "../core/SceneObject";
import { Logger } from '../utils/Logger';
import { ValidationError } from '../scenedata/behaviordata';
import { Orientation } from '../scenedata/common';
import { cloneMaterial } from '../utils/materialClone';

const DEFAULT_FRAME = "https://storageacceausprod.blob.core.windows.net/trace-cms-models/NjM4NjgxNTg1NDc3MDIxODk3LTVmZjE3ODkzLTRjMGEtNGI1MS05ODZhLTNhNjljNzY5NzNkZQ==.glb";

/** Scale factor matching the default guide frame GLB geometry */
const FRAME_SCALE_FACTOR = 2;
/** Border width (in meters) of the default guide frame GLB geometry */
const FRAME_IMPORTED_WIDTH = 0.05;

// 2024
// Author: Aaron Post

/**
 * Frame behavior that adds a customizable frame around images or other content.
 *
 * ## New Typed API
 *
 * ```typescript
 * const frame = sceneObject.getBehavior(Frame);
 * const data = frame.getData();
 * data.color = "#FF0000";
 * data.borderWidth = 10;
 * const result = await frame.commit(data);
 *
 * ```
 */
export class Frame extends TraceBehavior<FrameData> {
    static readonly behaviorType = "Frame" as const;

    // Runtime state (not serialized)
    public frameParts: { [key: string]: TransformNode | undefined };
    private isLoaded: boolean;
    /** The root mesh of the loaded frame GLB */
    public childMesh: TransformNode | null = null;
    /** Container cache path for cleanup */
    private _containerPath: string | undefined;
    /** Track the loaded src to detect resource changes in commit() */
    private _loadedSrc: string | undefined;
    /** The material this frame owns for its color override. */
    private _clonedMaterial: PBRMaterial | null = null;
    /** The GLB's authored albedo, captured before the first color override so a
     *  color that stops driving restores it instead of leaving the last one on. */
    private _authoredAlbedo: Color3 | null = null;
    /** Observers for graphic fit/orientation changes (for cleanup) */
    private _fitObserver: Observer<void> | null = null;
    private _orientationObserver: Observer<Orientation> | null = null;

    constructor(sceneObject: SceneObject, data: FrameData) {
        super(sceneObject, data);
        this.validate(this.getAuthored()); // Migrate borderColor -> color
        this._resolved = this._resolveAll();
        this.frameParts = {};
        this.isLoaded = false;
        this._loadedSrc = this._resolved.src;

        Logger.debug(`[Frame] Creating new Frame behavior for object: ${sceneObject.name}, borderWidth: ${this._resolved.borderWidth}, color: ${this._resolved.color || 'none'}`);
    }

    /**
     * Validate and migrate frame data.
     * Handles backward compatibility for borderColor -> color migration.
     * Mutates data in-place for migrations.
     */
    protected validate(data: FrameData): ValidationError[] {
        // Migrate borderColor to color if present (backward compatibility)
        if (data.borderColor && !data.color) {
            data.color = data.borderColor;
            delete data .borderColor;
        }

        // Strip legacy internal properties that are no longer part of the spec
        delete (data as unknown as Record<string, unknown>)['scaleFactor'];
        delete (data as unknown as Record<string, unknown>)['importedWidth'];

        return [];
    }

    protected async applyCommit(_patch: Partial<FrameData>): Promise<void> {
        const srcChanged = this._resolved.src !== this._loadedSrc;

        if (srcChanged) {
            await this.load();
            if (this.attachedNode) {
                this.onAttach(this.attachedNode);
            }
            this._loadedSrc = this._resolved.src;
            return;
        }

        this.applyImmediateProperties();
    }

    /**
     * Apply immediate (non-reload) property changes.
     * Called after commit() when src hasn't changed.
     */
    private applyImmediateProperties(): void {
        this._applyColor(drivingHexColor(this._resolved.color, 'Frame.color'));

        // Re-adjust frame dimensions if needed
        const graphicBehavior = this.sceneObject.getBehavior(Graphic);
        if (graphicBehavior) {
            this.adjustFrame(graphicBehavior);
        }
    }

    /**
     * Apply a color override to the GLB materials on each frame part mesh.
     * Clones shared materials on first call so other instances aren't affected.
     */
    /**
     * Apply a color override to the frame materials, or restore the authored
     * one when `hex` is null (the color stopped driving).
     */
    private _applyColor(hex: string | null): void {
        if (hex === null) {
            // Nothing drives the color. Put back what the GLB authored — only
            // possible if we captured it, which we did iff we ever overrode.
            if (this._clonedMaterial && this._authoredAlbedo) {
                this._writeAlbedo(this._authoredAlbedo);
            }
            return;
        }
        const color = Color3.FromHexString(hex);

        if (!this._clonedMaterial) {
            // First color apply — find the shared GLB material, clone it once,
            // and assign the clone to all frame part meshes
            let source: PBRMaterial | null = null;
            for (const part in this.frameParts) {
                const partNode = this.frameParts[part];
                if (partNode && partNode.getChildren().length > 0) {
                    const mesh = partNode.getChildren()[0] as Mesh;
                    if (mesh.material instanceof PBRMaterial) {
                        source = mesh.material;
                        break;
                    }
                }
            }
            if (!source) return;

            const cloned = cloneMaterial(source, `${source.name}_${this.sceneObject.name}`);
            if (!cloned) return;
            this._authoredAlbedo = cloned.albedoColor?.clone() ?? null;
            cloned.albedoColor = color;

            for (const part in this.frameParts) {
                const partNode = this.frameParts[part];
                if (partNode && partNode.getChildren().length > 0) {
                    const mesh = partNode.getChildren()[0] as Mesh;
                    mesh.material = cloned;
                }
            }
            this._clonedMaterial = cloned;
        } else {
            // Already cloned — just update the color on our material
            this._writeAlbedo(color);
        }
        Logger.debug(`[Frame] Applied color ${hex} to frame materials`);
    }

    /** Write an albedo onto the cloned frame material (all parts share one). */
    private _writeAlbedo(color: Color3): void {
        for (const part in this.frameParts) {
            const partNode = this.frameParts[part];
            if (partNode && partNode.getChildren().length > 0) {
                const mesh = partNode.getChildren()[0] as Mesh;
                if (mesh.material instanceof PBRMaterial) {
                    mesh.material.albedoColor = color.clone();
                    break; // All parts share the same cloned material
                }
            }
        }
    }

    async load(): Promise<void> {
        // Dispose existing resources if re-loading (hot-reload path)
        if (this.isLoaded) {
            this._disposeFrame();
        }

        Logger.debug(`[Frame] Loading frame model for ${this.sceneObject.name}`);
        this.isLoaded = true;

        // An unresolved binding is not a model path. Falling through to the
        // default frame is the point of having one — treating the binding text
        // as a src loads nothing and leaves the object frameless.
        const src = drivesValue(this._resolved.src) ? this._resolved.src : DEFAULT_FRAME;

        try {
            const result = await loadModel(src, this.scene, this.resources, {
                parent: this.sceneObject.transform,
                resetTransform: true,
                pluginExtension: ".glb",
                cloneMeshes: true  // Frame needs clones to assign per-part materials
            });

            this.childMesh = result.root;
            this._containerPath = result.containerPath;

            Logger.debug(`[Frame] Loaded frame model for ${this.sceneObject.name}`);
        } catch (error) {
            Logger.warn(`[Frame] Failed to load frame model: ${String(error)}`);
        }
    }

    init(): void {
        Logger.debug(`[Frame] init() called for ${this.sceneObject.name} - childMesh exists: ${!!this.childMesh}, frameParts count: ${Object.keys(this.frameParts).length}, color: ${this._resolved.color}`);
        if(!this.childMesh) {
            Logger.error(`[Frame] Missing frame mesh for ${this.sceneObject.name}`);
            return;
        }

        // Only find frame parts if they haven't been found yet
        const hasFrameParts = Object.keys(this.frameParts).length > 0;
        Logger.debug(`[Frame] hasFrameParts: ${hasFrameParts}`);
        if (!hasFrameParts) {
            const frameTransform = this.childMesh;
            const allChildren = frameTransform.getChildren(undefined, false);
            Logger.debug(`[Frame] Found ${allChildren.length} children in frame model`);
            const framePartNames = ["Top", "Bottom", "Back", "Right", "Left", "Bottom Right", "Bottom Left", "Top Right", "Top Left"];

            // Extract the prefix from the first child's name (if it exists)
            const prefixRegex = new RegExp(`^${frameTransform.name}_`);

            framePartNames.forEach(partName => {
                // Search for a child node, stripping the prefix if it's included
                const found = allChildren.find((child: Node) => {
                    const strippedName = child.name.replace(prefixRegex, "");
                    return strippedName === partName;
                }) as TransformNode | undefined;
                this.frameParts[partName] = found;
                if (found) {
                    Logger.debug(`[Frame] Found frame part: ${partName}, has children: ${found.getChildren().length > 0}`);
                } else {
                    Logger.warn(`[Frame] Frame part not found: ${partName}`);
                }
            });
            Logger.debug(`[Frame] Total frame parts found: ${Object.keys(this.frameParts).filter(k => this.frameParts[k]).length}/9`);
        }

        // Apply color override to existing GLB materials if one drives
        this._applyColor(drivingHexColor(this._resolved.color, 'Frame.color'));
    }

    protected onAttach(target: TransformNode): void {
        Logger.debug(`[Frame] onAttach() called for ${this.sceneObject.name} - childMesh exists: ${!!this.childMesh}, frameParts count: ${Object.keys(this.frameParts).length}, color: ${this._resolved.color}`);
        if (!this.childMesh) {
            Logger.debug(`[Frame] No childMesh, returning from onAttach for ${this.sceneObject.name}`);
            return;
        }

        // Find the graphic behavior (Image or Video) to determine the correct parent
        // The frame should be a sibling of the graphic mesh, both under the same parent
        // This ensures they move together during animations while maintaining proper Z positioning
        const graphicBehavior = this.sceneObject.getBehavior(Graphic);
        let attachTarget: TransformNode = target; // Default to SceneObject transform

        if (graphicBehavior) {
            // Find the graphic mesh (quad) from SceneObject's children
            // The quad is added via sceneObject.addChild() and named "{objectName}_mesh"
            const sceneObjectChildren = this.sceneObject.getChildTransforms();
            const graphicMesh = sceneObjectChildren.find(child =>
                child.name === `${this.sceneObject.name}_mesh`
            );

            if (graphicMesh) {
                // Use the graphic mesh's parent as the attach target
                // This makes frame a sibling of the graphic mesh, both under the same parent
                const graphicParent = graphicMesh.parent;
                if (graphicParent instanceof TransformNode) {
                    attachTarget = graphicParent;
                }
                Logger.debug(`[Frame] Found graphic mesh: ${graphicMesh.name}, attaching frame as sibling under parent: ${attachTarget.name}`);
            } else {
                // All behaviors load() before any onAttach fires, so the mesh should always exist here
                Logger.warn(`[Frame] Graphic behavior exists but mesh not found for ${this.sceneObject.name}`);
            }
        } else {
            Logger.debug(`[Frame] No graphic behavior found, attaching frame to SceneObject transform`);
        }

        // Attach frame mesh as sibling of the graphic mesh, slightly behind it
        if (this.childMesh) {
            this.childMesh.parent = attachTarget;
            this.childMesh.position.z = -0.01;
            // Use Euler rotation (GLB may set rotationQuaternion)
            if (this.childMesh.rotationQuaternion) {
                this.childMesh.rotationQuaternion = null;
            }
            // Match the graphic's orientation so the frame rotates with the content
            this.applyOrientation(graphicBehavior?.orientation);
            Logger.debug(`[Frame] Attached to ${attachTarget.name}, z=${this.childMesh.position.z}`);
        }

        // Initialize frame parts and apply color if needed
        if (this.childMesh) {
            if (Object.keys(this.frameParts).length === 0) {
                this.init();
            } else {
                this._applyColor(drivingHexColor(this._resolved.color, 'Frame.color'));
            }
        }

        if (graphicBehavior) {
            this.adjustFrame(graphicBehavior);

            // Follow the picture: new media, fit mode, orientation or box resize
            this._fitObserver = graphicBehavior.onFitChangedObservable.add(() => this.adjustFrame(graphicBehavior));

            // Re-orient when the graphic's orientation changes (e.g. via commit)
            this._orientationObserver = graphicBehavior.onOrientationChangedObservable.add((orientation) => this.applyOrientation(orientation));
        }
    }

    /**
     * Apply the graphic's orientation to the frame mesh so it matches the content.
     */
    private applyOrientation(orientation?: Orientation): void {
        if (!this.childMesh) return;

        if (orientation === Orientation.Horizontal) {
            // Tilt frame to face up to match the graphic quad's facing direction
            this.childMesh.rotation = new Vector3(-Math.PI / 2, 0, 0);
        } else {
            // Vertical (default) — frame GLB faces forward natively
            this.childMesh.rotation = new Vector3(0, 0, 0);
        }
    }

    /**
     * Size the ring and backing to the picture the graphic shows, in the
     * node's local axes. Under the node, the box scales x and y unequally, so
     * thickness is measured against the box height: a uniformly scaled
     * object keeps its border, and all four sides of any box match.
     */
    adjustFrame(graphic: Graphic): void {
        Logger.debug("Adjusting frame");

        if(Object.keys(this.frameParts).length !== 9) {
            Logger.error(`WRONG NUMBER OF FRAME PARTS! PROVIDED ${Object.keys(this.frameParts).length} parts instead of 9`);
        }

        const frameParts = this.frameParts;
        const widthInMeters = (this._resolved.borderWidth || 3) * 0.01;
        const scaleFactor = FRAME_SCALE_FACTOR;
        const scaledWidth = widthInMeters / FRAME_IMPORTED_WIDTH;

        const { sx, sy } = graphic.getFittedRect();
        const box = this.sceneObject.transform.scaling;
        const boxWidth = Math.abs(box.x);
        const boxHeight = Math.abs(graphic.orientation === Orientation.Horizontal ? box.z : box.y);
        const thicknessX = boxWidth > 0 && boxHeight > 0 ? scaledWidth * boxHeight / boxWidth : scaledWidth;

        const _top = frameParts["Top"];
        const _bottom = frameParts["Bottom"];
        const _left = frameParts["Left"];
        const _right = frameParts["Right"];
        const _back = frameParts["Back"];
        const _topRight = frameParts["Top Right"];
        const _topLeft = frameParts["Top Left"];
        const _bottomLeft = frameParts["Bottom Left"];
        const _bottomRight = frameParts["Bottom Right"];

        if (_right) {
            Logger.debug("ADJUSTING RIGHT");
            _right.position = new Vector3(0.5 * sx, 0, 0);
            _right.scaling = new Vector3(thicknessX, sy / scaleFactor, _right.scaling.z);
        }
        if (_left) {
            _left.position = new Vector3(-0.5 * sx, 0, 0);
            _left.scaling = new Vector3(thicknessX, sy / scaleFactor, _left.scaling.z);
        }
        if (_top) {
            _top.position = new Vector3(0, 0.5 * sy, 0);
            _top.scaling = new Vector3(sx / scaleFactor, scaledWidth, _top.scaling.z);
        }
        if (_bottom) {
            _bottom.position = new Vector3(0, -0.5 * sy, 0);
            _bottom.scaling = new Vector3(sx / scaleFactor, scaledWidth, _bottom.scaling.z);
        }
        if (_topRight) {
            _topRight.position = new Vector3(0.5 * sx, 0.5 * sy, 0);
            _topRight.scaling = new Vector3(thicknessX, scaledWidth, _topRight.scaling.z);
        }
        if (_topLeft) {
            _topLeft.position = new Vector3(-0.5 * sx, 0.5 * sy, 0);
            _topLeft.scaling = new Vector3(thicknessX, scaledWidth, _topLeft.scaling.z);
        }
        if (_bottomLeft) {
            _bottomLeft.position = new Vector3(-0.5 * sx, -0.5 * sy, 0);
            _bottomLeft.scaling = new Vector3(thicknessX, scaledWidth, _bottomLeft.scaling.z);
        }
        if (_bottomRight) {
            _bottomRight.position = new Vector3(0.5 * sx, -0.5 * sy, 0);
            _bottomRight.scaling = new Vector3(thicknessX, scaledWidth, _bottomRight.scaling.z);
        }
        if (_back) {
            _back.scaling = new Vector3(sx / scaleFactor, sy / scaleFactor, _back.scaling.z);
        }
    }

    protected onDetach(): void {
        Logger.debug("Detaching and disposing frame");

        // Unsubscribe from graphic observables
        this._fitObserver?.remove();
        this._fitObserver = null;
        this._orientationObserver?.remove();
        this._orientationObserver = null;

        this._disposeFrame();
    }

    /**
     * Dispose frame mesh and release container.
     * Shared by onDetach() and hot-reload in commit().
     */
    private _disposeFrame(): void {
        Object.values(this.frameParts).forEach((part) => {
            if (part) {
                try { part.dispose(); } catch {}
            }
        });

        this.frameParts = {};
        this._clonedMaterial?.dispose();
        this._clonedMaterial = null;
        this._authoredAlbedo = null;

        if (this.childMesh) {
            try { this.childMesh.dispose(); } catch {}
        }
        this.childMesh = null;

        if (this._containerPath) {
            this.resources.releaseContainer(this._containerPath);
            this._containerPath = undefined;
        }
    }
}
